import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Upload, CheckCircle2, XCircle, ArrowUpRight, Loader2, FileSpreadsheet } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarketingUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dedupe, setDedupe] = useState("email");
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/marketing/token/${token}`)
      .then(({ data }) => { setInfo(data); setDedupe(data.default_dedupe || "email"); })
      .catch(() => setNotFound(true));
  }, [token]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/public/marketing/upload?token=${token}&dedupe_by=${dedupe}`, fd);
      setResult(data);
      toast.success(`${data.inserted} lead${data.inserted === 1 ? "" : "s"} imported`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
      if (err?.response?.status === 401) setNotFound(true);
    }
    setUploading(false);
    // Reset input so the same file can be re-uploaded if needed
    if (fileRef.current) fileRef.current.value = "";
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[color:var(--aii-bg)] flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <img src="/aii-logo.png" alt="Aii Group" className="w-16 h-16 mx-auto object-contain mb-4"/>
          <XCircle className="mx-auto text-red-500" size={36}/>
          <h1 className="text-xl font-display font-semibold mt-3">This upload link is invalid or has been revoked.</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-2">Please ask the BDOM at Aii Group for a fresh link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--aii-bg)]">
      <header className="bg-white border-b border-[color:var(--aii-border)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <img src="/aii-logo.png" alt="Aii Group" className="w-10 h-10 object-contain"/>
          <div className="min-w-0">
            <div className="font-display text-base font-bold">AII GROUP<span className="text-brand">®</span></div>
            <div className="text-xs text-[color:var(--aii-muted)]">Meaningful Adventures</div>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-brand font-semibold">Marketing → BDOM</div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold mt-1 flex items-center gap-2"><Upload className="text-brand"/> Drop your leads sheet here</h1>
          {info && (
            <p className="text-sm text-[color:var(--aii-text-2)] mt-2">
              Welcome, <b>{info.label}</b>. Your uploads land directly in {info.created_by_name || "the BDOM"}&rsquo;s Leads pipeline &mdash; no login required.
            </p>
          )}
        </div>

        <div className="aii-card p-6" data-testid="marketing-upload">
          <div className="mb-4 text-sm text-[color:var(--aii-text-2)]">
            Accepted formats: <b>.xlsx</b>, <b>.xls</b>, <b>.csv</b> &middot; up to <b>10 MB</b>. Columns matched automatically: <span className="font-mono text-xs bg-[color:var(--aii-green-tint)] px-1 rounded">Name, Age, Gender, Email, Phone, Country, Interested Program, Source, Notes</span>. Aliases like &ldquo;Full Name&rdquo;, &ldquo;Mobile&rdquo;, &ldquo;Programme&rdquo;, &ldquo;Campaign&rdquo; also work.
          </div>

          <label className="block mb-4">
            <div className="text-xs text-[color:var(--aii-muted)] mb-1">Skip duplicates by</div>
            <select value={dedupe} onChange={(e) => setDedupe(e.target.value)} className="input-aii w-full" data-testid="dedupe">
              <option value="email">Email address (default)</option>
              <option value="phone">Phone number</option>
              <option value="none">Don&rsquo;t skip — import everything</option>
            </select>
          </label>

          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={upload} data-testid="file"/>
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="upload-btn"
            className="w-full py-4 border-2 border-dashed border-[color:var(--aii-border)] rounded-lg text-center hover:border-brand hover:bg-[color:var(--aii-green-tint)] disabled:opacity-50 flex flex-col items-center gap-1">
            {uploading ? <><Loader2 className="animate-spin text-brand" size={24}/><span className="text-sm font-semibold mt-1">Uploading…</span></>
              : <><FileSpreadsheet className="text-brand" size={28}/><span className="text-sm font-semibold mt-1">Click to choose a file</span><span className="text-xs text-[color:var(--aii-muted)]">or drag-and-drop coming soon</span></>}
          </button>

          {result && (
            <div className="mt-4 p-4 bg-[color:var(--aii-green-tint)] rounded" data-testid="upload-result">
              <div className="flex items-center gap-2 text-brand-dark font-semibold"><CheckCircle2 size={16}/> {result.inserted} lead{result.inserted === 1 ? "" : "s"} imported.</div>
              <div className="text-xs mt-1 text-[color:var(--aii-text-2)]">Skipped as duplicates: {result.skipped}</div>
              {result.mapping && (
                <div className="text-[10px] text-[color:var(--aii-muted)] mt-2 font-mono">
                  Mapped: {Object.entries(result.mapping).map(([k, v]) => `${k}=${v}`).join(", ")}
                </div>
              )}
              {result.errors?.length > 0 && (
                <details className="text-xs text-red-700 mt-2">
                  <summary>{result.errors.length} row error(s)</summary>
                  <ul className="pl-4 list-disc mt-1">{result.errors.map((e) => <li key={`row-${e.row}-${e.error?.slice(0,20)}`}>Row {e.row}: {e.error}</li>)}</ul>
                </details>
              )}
            </div>
          )}
        </div>

        <p className="text-xs text-[color:var(--aii-muted)] mt-6 text-center">
          Uploads are private and go straight to the BDOM&rsquo;s Leads inbox. Do not share this link outside the Marketing team.
        </p>
      </div>

      <footer className="bg-white border-t border-[color:var(--aii-border)] mt-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between gap-3 text-xs text-[color:var(--aii-muted)]">
          <div>Aii Group&#174; &middot; Jaipur, India</div>
          <a href="https://theaiigroupteam.com" target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-brand">theaiigroupteam.com <ArrowUpRight size={12}/></a>
        </div>
      </footer>
    </div>
  );
}
